import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  stickyFilter: boolean;
  toggleStickyFilter: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const getInitialTheme = (): Theme => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const storedPrefs = window.localStorage.getItem('theme');
    if (typeof storedPrefs === 'string') {
      return storedPrefs as Theme;
    }

    const userMedia = window.matchMedia('(prefers-color-scheme: dark)');
    if (userMedia.matches) {
      return 'dark';
    }
  }
  return 'light';
};

const getInitialSticky = (): boolean => {
    if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('stickyFilter');
        if (stored !== null) {
            return JSON.parse(stored);
        }
    }
    return true; // Default to sticky
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [stickyFilter, setStickyFilter] = useState<boolean>(getInitialSticky);

  useEffect(() => {
    const root = window.document.documentElement;
    const isDark = theme === 'dark';

    root.classList.remove(isDark ? 'light' : 'dark');
    root.classList.add(theme);

    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  const toggleStickyFilter = () => {
      setStickyFilter(prev => {
          const newVal = !prev;
          localStorage.setItem('stickyFilter', JSON.stringify(newVal));
          return newVal;
      });
  };

  const value = useMemo(
    () => ({ theme, toggleTheme, stickyFilter, toggleStickyFilter }),
    [theme, stickyFilter]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
